import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  LoginRequest,
  SignupRequest,
  AuthResponse,
  User,
  ApiResponse,
  GoogleTokenRequest
} from '../models/user.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = `${environment.apiUrl}/auth`;
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'user';

  private currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {}

  signup(request: SignupRequest): Observable<ApiResponse<AuthResponse>> {
    return this.http.post<ApiResponse<AuthResponse>>(`${this.API_URL}/signup`, request)
      .pipe(
        tap(response => {
          if (response.success) {
            this.handleAuthSuccess(response.data);
          }
        })
      );
  }

  login(request: LoginRequest): Observable<ApiResponse<AuthResponse>> {
    return this.http.post<ApiResponse<AuthResponse>>(`${this.API_URL}/login`, request)
      .pipe(
        tap(response => {
          if (response.success) {
            this.handleAuthSuccess(response.data);
          }
        })
      );
  }

  loginWithGoogle(idToken: string): Observable<ApiResponse<AuthResponse>> {
    const request: GoogleTokenRequest = { idToken };
    return this.http.post<ApiResponse<AuthResponse>>(`${this.API_URL}/oauth/google`, request)
      .pipe(
        tap(response => {
          if (response.success) {
            this.handleAuthSuccess(response.data);
          }
        })
      );
  }

  logout(): Observable<ApiResponse<void>> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<ApiResponse<void>>(`${this.API_URL}/logout`, { refreshToken })
      .pipe(
        tap(() => {
          this.clearStorage();
          this.currentUserSubject.next(null);
        })
      );
  }

  getCurrentUser(): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${this.API_URL}/me`)
      .pipe(
        tap(response => {
          if (response.success) {
            this.setUser(response.data);
          }
        })
      );
  }

  refreshToken(): Observable<ApiResponse<AuthResponse>> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<ApiResponse<AuthResponse>>(`${this.API_URL}/refresh`, { refreshToken })
      .pipe(
        tap(response => {
          if (response.success) {
            this.setAccessToken(response.data.accessToken);
            // Keep the same refresh token
          }
        })
      );
  }

  forgotPassword(email: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.API_URL}/forgot-password`, { email });
  }

  validateResetToken(token: string): Observable<ApiResponse<{ valid: boolean }>> {
    return this.http.post<ApiResponse<{ valid: boolean }>>(`${this.API_URL}/validate-reset-token`, { token });
  }

  resetPassword(token: string, newPassword: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.API_URL}/reset-password`, { token, newPassword });
  }

  verifyEmail(token: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.API_URL}/verify-email?token=${token}`, {});
  }

  resendVerificationEmail(email: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.API_URL}/resend-verification`, { email });
  }

  private handleAuthSuccess(authResponse: AuthResponse): void {
    this.setAccessToken(authResponse.accessToken);
    this.setRefreshToken(authResponse.refreshToken);
    this.setUser(authResponse.user);
  }

  // Token management
  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  private setAccessToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  // User management
  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private getUserFromStorage(): User | null {
    const userJson = localStorage.getItem(this.USER_KEY);
    return userJson ? JSON.parse(userJson) : null;
  }

  getCurrentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUserValue();
    return user ? user.roles.includes(role) : false;
  }

  isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  private clearStorage(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }
}
